import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Download, ArrowRight, Leaf, MapPin, Globe2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/lib/motion";
import InquiryForm from "@/components/InquiryForm";
import { getProduct } from "@/lib/api";

const SPEC_KEYS = [
  ["origin", "Origin"], ["purity", "Purity"], ["moisture", "Moisture"],
  ["shelf_life", "Shelf Life"], ["moq", "MOQ"], ["container_capacity", "Container Capacity"],
  ["lead_time", "Lead Time"], ["export_availability", "Export Availability"],
];

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setData(null); setNotFound(false); setActiveImg(0);
    getProduct(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const p = data.product;
    document.title = `${p.name} — Organic ${p.category} Exporter India | Karmaa Overseas`;
    setMeta("description", `${p.name}${p.name_hi ? " (" + p.name_hi + ")" : ""} — export-grade organic ${p.category.toLowerCase()} from India. ${p.description.slice(0, 120)}`);
    const ld = {
      "@context": "https://schema.org", "@type": "Product", name: p.name, image: p.gallery || [p.image],
      description: p.description, category: p.category,
      brand: { "@type": "Brand", name: "Karmaa Overseas" },
      countryOfOrigin: "India",
    };
    let el = document.getElementById("product-ld");
    if (!el) { el = document.createElement("script"); el.id = "product-ld"; el.type = "application/ld+json"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(ld);
    return () => { document.title = "Karmaa Overseas | Premium Indian Agri Exporter"; };
  }, [data]);

  if (notFound) return (
    <main className="pt-40 pb-32 text-center bg-beige min-h-screen">
      <p className="font-serif text-4xl text-forest">Product not found.</p>
      <Link to="/products" className="text-gold underline mt-4 inline-block">Back to products</Link>
    </main>
  );

  if (!data) return <main className="pt-40 pb-32 bg-beige min-h-screen"><div className="max-w-7xl mx-auto px-6 h-96 bg-forest/5 animate-pulse" /></main>;

  const { product: p, related } = data;
  const gallery = p.gallery && p.gallery.length ? p.gallery : [p.image];

  return (
    <main className="bg-beige" data-testid="product-detail">
      {/* Hero */}
      <section className="relative pt-32">
        <div className="grid lg:grid-cols-2">
          <div className="relative h-[50vh] lg:h-[80vh] overflow-hidden bg-forest/5">
            <motion.img key={activeImg} initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              src={gallery[activeImg]} alt={`Organic ${p.name} — ${["product","harvest","packaging"][activeImg] || "view"} | Karmaa Overseas India`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/40 to-transparent pointer-events-none" />
            {p.organic && <span className="absolute top-6 left-6 text-[10px] uppercase tracking-widest font-semibold bg-olive text-beige px-3 py-1.5 rounded-full">Organic · Export Grade</span>}
            {gallery.length > 1 && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-3">
                {gallery.map((g, i) => (
                  <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setActiveImg(i)}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? "border-gold scale-110" : "border-beige/50 opacity-70 hover:opacity-100"}`}>
                    <img src={g} alt={`${p.name} view ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="bg-forest text-beige p-10 lg:p-20 flex flex-col justify-center grain relative">
            <div className="relative z-10">
              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-beige/60 text-xs mb-6" data-testid="breadcrumb">
                <Link to="/" className="hover:text-gold">Home</Link><ChevronRight className="w-3 h-3" />
                <Link to="/products" className="hover:text-gold">Products</Link><ChevronRight className="w-3 h-3" />
                <Link to={`/products?category=${p.category_slug}`} className="hover:text-gold">{p.category}</Link><ChevronRight className="w-3 h-3" />
                <span className="text-gold">{p.name}</span>
              </nav>
              <h1 className="font-serif text-6xl md:text-7xl leading-none mb-2">{p.name}</h1>
              {p.name_hi && <p className="font-serif text-3xl text-gold/90 mb-4">{p.name_hi}</p>}
              <div className="flex flex-wrap gap-2 mb-5">
                {p.sizes.map((sz) => (
                  <span key={sz} className="text-[10px] uppercase tracking-wider border border-beige/25 rounded-full px-3 py-1 text-beige/80">{sz}</span>
                ))}
              </div>
              <p className="text-beige/75 font-light leading-relaxed max-w-lg">{p.description}</p>
              <div className="flex flex-wrap gap-4 mt-10">
                <a href="#inquiry" data-testid="inquire-btn" className="group flex items-center gap-3 bg-gold text-forest px-7 py-3.5 text-sm uppercase tracking-widest font-medium rounded-full hover:bg-beige transition-colors">
                  Contact for Bulk Orders <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <button data-testid="download-catalogue" onClick={() => toast.success("Catalogue request noted — we'll email it shortly.")}
                  className="flex items-center gap-3 border border-beige/40 rounded-full px-7 py-3.5 text-sm uppercase tracking-widest hover:bg-beige hover:text-forest transition-colors">
                  <Download className="w-4 h-4" /> Catalogue
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits + Nutrition */}
      <section className="max-w-7xl mx-auto px-6 pt-24 grid lg:grid-cols-2 gap-12">
        <Reveal className="card-soft p-8" data-testid="benefits">
          <h2 className="font-serif text-3xl text-forest mb-6 flex items-center gap-3"><Leaf className="w-6 h-6 text-olive" /> Health Benefits</h2>
          <ul className="space-y-3">
            {p.health_benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-ink/70"><Check className="w-4 h-4 text-gold mt-1 shrink-0" /> {b}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1} className="card-soft p-8" data-testid="nutrition">
          <h2 className="font-serif text-3xl text-forest mb-6">Nutritional Information</h2>
          <div className="grid grid-cols-2 gap-px bg-forest/10 border border-forest/10 rounded-xl overflow-hidden">
            {p.nutrition.map((n) => (
              <div key={n.label} className="bg-white p-4">
                <p className="overline text-olive text-[10px] mb-1">{n.label}</p>
                <p className="font-serif text-xl text-forest">{n.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3 text-sm text-ink/60"><MapPin className="w-4 h-4 text-gold" /> Country of Origin: <span className="text-forest font-medium">{p.country_of_origin}</span></div>
        </Reveal>
      </section>

      {/* Specs + applications */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          <Reveal><h2 className="font-serif text-4xl text-forest mb-8">Specifications</h2></Reveal>
          <div className="grid sm:grid-cols-2 gap-px bg-forest/10 border border-forest/10">
            {SPEC_KEYS.map(([k, label]) => (
              <div key={k} className="bg-beige p-5">
                <p className="overline text-olive text-[10px] mb-1">{label}</p>
                <p className="font-serif text-2xl text-forest">{p[k]}</p>
              </div>
            ))}
            {(p.extra_specs || []).map((s) => (
              <div key={s.label} className="bg-beige p-5" data-testid={`spec-${s.label}`}>
                <p className="overline text-olive text-[10px] mb-1">{s.label}</p>
                <p className="font-serif text-2xl text-forest">{s.value}</p>
              </div>
            ))}
          </div>

          <Reveal><h3 className="font-serif text-3xl text-forest mt-14 mb-6">Grades</h3></Reveal>
          <div className="flex flex-wrap gap-3">
            {p.grades.map((g) => (
              <span key={g} className="px-5 py-2 border border-forest/20 text-forest text-sm">{g}</span>
            ))}
          </div>

          <Reveal><h3 className="font-serif text-3xl text-forest mt-14 mb-6">Packaging</h3></Reveal>
          <ul className="grid sm:grid-cols-2 gap-3">
            {p.packaging.map((pk) => (
              <li key={pk} className="flex items-center gap-3 text-ink/70"><Check className="w-4 h-4 text-gold" /> {pk}</li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 bg-white border border-forest/10 p-8">
            <h3 className="font-serif text-2xl text-forest mb-6">Applications</h3>
            <div className="space-y-3">
              {p.applications.map((a, i) => (
                <div key={a} className="flex items-center gap-4 border-b border-forest/10 pb-3">
                  <span className="font-serif text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-forest">{a}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Varieties & Grades */}
      {p.varieties && p.varieties.length > 0 && (
        <section className="bg-forest text-beige py-24 grain relative overflow-hidden" data-testid="varieties">
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <Reveal><p className="overline text-gold mb-4">Varieties & Grades</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl mb-4 max-w-3xl leading-tight">
                Choose the exact <span className="italic text-gold">variety & grade</span> your market demands.
              </h2>
            </Reveal>
            <p className="text-beige/60 text-sm mb-12 max-w-xl font-light">
              Every variety below is available for export with its own specification sheet, packaging and pricing.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {p.varieties.map((v, i) => (
                <Reveal key={v.name} delay={(i % 3) * 0.08} className="bg-beige text-ink group" >
                  <div className="overflow-hidden h-52" data-testid={`variety-${i}`}>
                    <img src={v.image} alt={v.name} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl text-forest leading-tight">{v.name}</h3>
                    {v.tagline && <p className="text-ink/60 text-sm mt-2 font-light">{v.tagline}</p>}
                    <div className="mt-5 space-y-2">
                      {v.specs.map((s) => (
                        <div key={s.label} className="flex justify-between text-sm border-b border-forest/10 pb-1.5">
                          <span className="text-ink/50">{s.label}</span>
                          <span className="text-forest font-medium text-right">{s.value}</span>
                        </div>
                      ))}
                    </div>
                    <a href="#inquiry" className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-forest link-sweep">
                      Enquire this grade <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Storage, Quality & Export Markets */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-8">
        <Reveal className="card-soft p-8" data-testid="storage">
          <h3 className="font-serif text-2xl text-forest mb-4">Storage Instructions</h3>
          <p className="text-ink/70 font-light leading-relaxed">{p.storage}</p>
        </Reveal>
        <Reveal delay={0.08} className="card-soft p-8">
          <h3 className="font-serif text-2xl text-forest mb-4">Quality Standards</h3>
          <ul className="space-y-2 text-sm text-ink/70">
            {["FSSAI & APEDA Registered", "ISO 22000 · HACCP · GMP", "Sortex Cleaned & Lab Tested", "Full Export Documentation (COA, COO, Phyto)"].map((q) => (
              <li key={q} className="flex items-start gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {q}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.16} className="card-soft p-8" data-testid="export-markets">
          <h3 className="font-serif text-2xl text-forest mb-4 flex items-center gap-2"><Globe2 className="w-5 h-5 text-olive" /> Export Markets</h3>
          <div className="flex flex-wrap gap-2">
            {p.export_markets.map((m) => (
              <span key={m} className="text-xs border border-forest/20 rounded-full px-3 py-1 text-forest">{m}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Inquiry */}
      <section id="inquiry" className="bg-white py-24" data-testid="product-inquiry">
        <div className="max-w-4xl mx-auto px-6">
          <p className="overline text-olive mb-4 text-center">Request a Quote</p>
          <h2 className="font-serif text-5xl text-forest text-center mb-12">Enquire about {p.name}</h2>
          <InquiryForm presetProduct={p.name} />
        </div>
      </section>

      {/* FAQ */}
      {p.faqs && p.faqs.length > 0 && (
        <section className="bg-white py-20" data-testid="product-faq">
          <div className="max-w-4xl mx-auto px-6">
            <p className="overline text-olive mb-4 text-center">FAQ</p>
            <h2 className="font-serif text-4xl text-forest text-center mb-10">Buyer questions about {p.name}</h2>
            <Accordion type="single" collapsible className="w-full">
              {p.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="border-b border-forest/15" data-testid={`pfaq-${i}`}>
                  <AccordionTrigger className="font-serif text-xl text-forest text-left hover:text-gold hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-ink/70 font-light leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="font-serif text-4xl text-forest mb-10">Related Products</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((r) => (
              <Link key={r.slug} to={`/products/${r.slug}`} data-testid={`related-${r.slug}`} className="group block">
                <div className="overflow-hidden h-56 mb-3">
                  <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <h3 className="font-serif text-2xl text-forest group-hover:text-gold transition-colors">{r.name}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
